import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { SlideOver } from '@/components/shared';
import type { DataUpdateRequest, DayOffRequest, DpRequest, DpRequestMessage, TicketRequest } from '../../../../../shared/dpRequests';
import { ChangeReview } from '../ChangeReview';
import { AttachmentList } from '../AttachmentList';
import { StatusPill } from '../StatusPill';
import { TicketThread } from '../TicketThread';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { ClientApprovalSummary } from '../RequestSummaries';
import { MyHealthPlanDetail, MyOvertimeDetail, MyRaroInvestDetail } from './NewTypeDetails';
import { REQUEST_TYPE_LABELS, describeRequestStatus } from '../../dpRequests.definitions';
import { formatDayOffDates } from '../../utils/formatDayOffDates';
import { canCancelDayOff } from '../../utils/dayOffPolicy';
import { todayIsoDate } from '../../utils/dayOffPolicy';
import { useCancelDataUpdate, useCancelDayOff, useCancelTicket, usePostTicketMessage } from '../../hooks/data/useDpRequestMutations';
import { useDpMessagesQuery, useDpRequestQuery } from '../../hooks/data/useDpRequestQueries';
import { DATA_UPDATE_PATH } from '../../routes';
import type { MyRequestDrawerProps } from './myRequestDrawer.types';

export function MyRequestDrawer({ requestId, viewerEmail, onClose }: MyRequestDrawerProps) {
    const requestQuery = useDpRequestQuery(requestId);
    const request = requestQuery.data;
    const isTicket = request?.type === 'ticket';
    const messagesQuery = useDpMessagesQuery(isTicket ? requestId : null);

    return (
        <SlideOver
            isOpen={Boolean(requestId)}
            onClose={onClose}
            title={request ? titleOf(request) : 'Pedido'}
            subtitle={request ? REQUEST_TYPE_LABELS[request.type] : undefined}
        >
            {requestQuery.isPending && <div className="h-40 animate-pulse rounded-xl bg-raro-gray-100" />}
            {requestQuery.isError && <p className="text-sm text-rose-700">Não foi possível carregar este pedido.</p>}
            {request?.type === 'data_update' && <DataUpdateDetail request={request} />}
            {request?.type === 'ticket' && (
                <TicketDetail request={request} viewerEmail={viewerEmail} messages={messagesQuery} />
            )}
            {request?.type === 'day_off' && <DayOffDetail request={request} />}
            {request?.type === 'overtime' && <MyOvertimeDetail request={request} />}
            {request?.type === 'raro_invest' && <MyRaroInvestDetail request={request} />}
            {request?.type === 'health_plan' && <MyHealthPlanDetail request={request} />}
        </SlideOver>
    );
}

function titleOf(request: DpRequest): string {
    if (request.type === 'ticket') return request.category === 'reserved' ? 'Chamado reservado' : request.subject;
    return REQUEST_TYPE_LABELS[request.type];
}

function DataUpdateDetail({ request }: { request: DataUpdateRequest }) {
    const cancel = useCancelDataUpdate();
    const meta = describeRequestStatus(request);
    const open = request.status === 'pending_dp' || request.status === 'pending_correction';

    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />

            {request.decision?.note && <Note title="Mensagem do DP" text={request.decision.note} />}

            <section className="space-y-3">
                <h4 className="text-sm font-semibold text-raro-gray-800">O que foi pedido</h4>
                <ChangeReview changes={request.changes} />
                <AttachmentList attachments={request.attachments} />
            </section>

            <div className="flex flex-col gap-3 border-t border-raro-gray-200 pt-5 sm:flex-row sm:justify-end">
                {request.status === 'pending_correction' && (
                    <Link
                        to={DATA_UPDATE_PATH}
                        className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
                    >
                        Corrigir e reenviar
                    </Link>
                )}
                {open && (
                    <Button variant="outline" loading={cancel.isPending} onClick={() => void cancel.mutateAsync(request.id).catch(() => undefined)}>
                        Cancelar pedido
                    </Button>
                )}
            </div>
        </div>
    );
}

function TicketDetail({
    request,
    viewerEmail,
    messages,
}: {
    request: TicketRequest;
    viewerEmail: string;
    messages: { data?: DpRequestMessage[]; isPending: boolean };
}) {
    const { user } = useAuth();
    const post = usePostTicketMessage();
    const cancel = useCancelTicket();
    const [confirming, setConfirming] = useState(false);
    const meta = describeRequestStatus(request);
    const canCancel = request.status === 'open' || request.status === 'waiting_collaborator';
    const reserved = request.category === 'reserved';

    async function send(body: string, files: PendingFile[]) {
        if (!user?.uid) return;
        await withUploadedAttachments(files, user.uid, attachments =>
            post.mutateAsync({ requestId: request.id, body, attachments })
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill label={meta.label} tone={meta.tone} />
                {reserved && <span className="text-xs text-raro-gray-500">Só você e o DP veem esta conversa.</span>}
            </div>

            <TicketThread
                messages={messages.data ?? []}
                viewerEmail={viewerEmail}
                isLoading={messages.isPending}
                canReply={request.status !== 'cancelled'}
                replyPlaceholder="Escreva sua mensagem para o DP"
                isSending={post.isPending}
                onSend={send}
            />

            {canCancel && (
                <div className="flex justify-end border-t border-raro-gray-200 pt-5">
                    {confirming ? (
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={cancel.isPending}>
                                Voltar
                            </Button>
                            <Button variant="danger" size="sm" loading={cancel.isPending} onClick={() => void cancel.mutateAsync(request.id).catch(() => undefined)}>
                                Sim, cancelar chamado
                            </Button>
                        </div>
                    ) : (
                        <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
                            Cancelar chamado
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

function DayOffDetail({ request }: { request: DayOffRequest }) {
    const cancel = useCancelDayOff();
    const meta = describeRequestStatus(request);
    const cancellable = canCancelDayOff(request, todayIsoDate());

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill label={meta.label} tone={meta.tone} />
                <span className="text-xs text-raro-gray-500">{request.totalDays.toString().replace('.', ',')} dia(s)</span>
            </div>

            <section className="space-y-2">
                <h4 className="text-sm font-semibold text-raro-gray-800">Datas</h4>
                <ul className="space-y-1 text-sm text-raro-gray-700">
                    {formatDayOffDates(request.days).map(line => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            </section>

            <ClientApprovalSummary request={request} />

            {request.reason && <Note title="Motivo" text={request.reason} />}
            {request.dpRegistration && (
                <p className="text-sm font-medium text-emerald-700">Já lançado no ponto pelo DP.</p>
            )}

            {cancellable && (
                <div className="flex justify-end border-t border-raro-gray-200 pt-5">
                    <Button variant="outline" loading={cancel.isPending} onClick={() => void cancel.mutateAsync(request.id).catch(() => undefined)}>
                        Cancelar day-off
                    </Button>
                </div>
            )}
        </div>
    );
}

function Note({ title, text }: { title: string; text: string }) {
    return (
        <div className="rounded-xl border border-raro-gray-200 bg-raro-gray-50 px-4 py-3 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-raro-gray-500">{title}</p>
            <p className="mt-1 whitespace-pre-wrap text-raro-gray-800">{text}</p>
        </div>
    );
}
